'use client'

import { useId } from 'react'
import { autoBetSkipText, autoBetZoneText, birdieAutoBetCopy } from './autoBetCopy'
import type { AutoBetRule, AutoBetSkip } from './birdieAutoBet'
import type { Translate } from './tileViewModel'

type Props = Readonly<{
  rule: AutoBetRule
  /** Why the open round was skipped, for the length of its window; the armed line otherwise. */
  skip: AutoBetSkip | undefined
  formatMultiplier: (m: number) => string
  formatAmount: (amount: number) => string
  onOpen: () => void
  /** The cross: off, without a trip through the sheet. */
  onStop: () => void
  t: Translate
}>

/**
 * The armed pill (B32), over the stream: a live dot, the zone in tabular
 * numerals, the markets, the rounds left — and a cross that ends the rule
 * where it stands. It sits on top of live video, so it is a quiet capsule and
 * not a badge: it has to be readable at a glance without competing with the
 * putt behind it.
 *
 * Two buttons, not one: a cross nested inside the opener would be a button in
 * a button. The capsule itself is the frame, the line is the opener, and the
 * whole line is that button's description.
 *
 * For the window of a round the rule stood down on, the line is the reason
 * instead — the quiet state, dot out, not an alarm.
 */
export function BirdieAutoBetPill({
  rule,
  skip,
  formatMultiplier,
  formatAmount,
  onOpen,
  onStop,
  t
}: Props) {
  const copy = birdieAutoBetCopy(t)
  const textId = useId()
  const zone = autoBetZoneText(copy, rule.zone, formatMultiplier)

  return (
    <div
      className={`birdie-autobet-pill${skip === undefined ? '' : ' birdie-autobet-pill--skipped'}`}
    >
      <button
        aria-describedby={textId}
        aria-label={copy.ariaOpen}
        className="birdie-autobet-pill__open"
        onClick={onOpen}
        type="button"
      >
        <span aria-hidden="true" className="birdie-autobet-pill__dot" />
        {/*
          B40: the line REMOUNTS when the pill changes state — armed to stood
          down and back — so the swap reads as the rule doing something rather
          than as text being replaced under the player. A key is the only way to
          replay a CSS one-shot on an element that otherwise persists, and this
          pill persists for the life of the rule.
        */}
        <span
          className="birdie-autobet-pill__text"
          id={textId}
          key={skip === undefined ? 'armed' : 'stood-down'}
        >
          {skip === undefined ? (
            <>
              <span className="birdie-autobet-pill__part birdie-autobet-pill__part--zone">
                {zone}
              </span>
              <span className="birdie-autobet-pill__part">{copy.markets[rule.markets]}</span>
              {/*
                …and the rounds-left figure remounts on its own count, so a round
                being spent lands with a little weight instead of the number
                simply being different the next time the player looks.
              */}
              <span
                className="birdie-autobet-pill__part birdie-autobet-pill__part--left"
                key={`left-${String(rule.roundsLeft)}`}
              >
                {rule.roundsLeft === null ? copy.pillUntilStopped : copy.pillLeft(rule.roundsLeft)}
              </span>
            </>
          ) : (
            <span className="birdie-autobet-pill__part">
              {autoBetSkipText(copy, skip, { zone, ceiling: formatAmount(rule.roundCeiling) })}
            </span>
          )}
        </span>
      </button>
      <button
        aria-label={copy.stop}
        className="birdie-autobet-pill__stop"
        onClick={onStop}
        type="button"
      >
        <span aria-hidden="true">×</span>
      </button>
    </div>
  )
}
